import { Component, type OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import type { MatcherComponentRegistryService } from '@cdw-ae/filter-bar-core';
import type { FieldMatcher } from '@cdw-ae/filter-bar-interfaces';
import { TextMatcherComponent } from '@cdw-ae/filter-bar-text-matchers';
import { combineLatest, map, startWith } from 'rxjs';

export const NumberMatcher: FieldMatcher = {
  name: 'numberMatch',
  displayName: 'Number Comparison',
  hasEditor: true,
  options: {
    chipFormatter: ((value: { operator: string; value: number }) =>
      `${value.operator} ${value.value}`) as unknown as (
      arg0: string
    ) => string,
  },
  validators: [
    {
      errorKey: 'notANumber',
      errorMessage: 'Value must be a number',
      validator: (control) => {
        const value = control.value?.value;
        return Number.isNaN(value) ? { notANumber: true } : null;
      },
    },
  ],
};

@Component({
  selector: 'cdw-number-matcher',
  template: `
    <div [formGroup]="state" class="number-matcher-form">
      <mat-form-field appearance="fill" class="operator-field">
        <mat-label>Operator</mat-label>
        <mat-select formControlName="operatorControl">
          <mat-option value="=">Equals</mat-option>
          <mat-option value=">">Greater Than</mat-option>
          <mat-option value="<">Less Than</mat-option>
          <mat-option value=">=">Greater Than or Equal</mat-option>
          <mat-option value="<=">Less Than or Equal</mat-option>
        </mat-select>
      </mat-form-field>

      <mat-form-field appearance="fill" class="value-field">
        <mat-label>Value</mat-label>
        <input formControlName="textControl" matInput type="number" />
        <mat-error class="matcher-error" *ngFor="let error of errorMessages">
          {{ error }}
        </mat-error>
      </mat-form-field>
    </div>
  `,
  styleUrls: ['./text-matcher/text.matcher.scss'],
  standalone: false,
})
export class NumberMatcherComponent
  extends TextMatcherComponent
  implements OnInit
{
  override ngOnInit(): void {
    // Add the new operator control to the form
    this.state.addControl('operatorControl', new FormControl('='));

    this.state.controls.textControl.setValidators([
      Validators.required,
      (control) => {
        const value = control.value;
        if (value === null || value === undefined || value === '') {
          return { required: true };
        }
        return Number.isNaN(Number(value)) ? { notANumber: true } : null;
      },
    ]);

    // Handle initial value - could be string, number, or object
    const matchValue = this.matcherOptions.matchValue;
    let initialOperator = '=';
    let initialValue = '';

    if (
      matchValue &&
      typeof matchValue === 'object' &&
      'operator' in matchValue &&
      'value' in matchValue
    ) {
      // It's already in the correct format
      const typedMatchValue = matchValue as { operator: string; value: number };
      initialOperator = typedMatchValue.operator;
      initialValue = typedMatchValue.value?.toString() ?? '';
    } else if (matchValue !== null && matchValue !== undefined) {
      // It's a primitive value, use default operator
      initialValue = matchValue.toString();
    }

    this.state.controls.operatorControl.setValue(initialOperator);
    this.state.controls.textControl.setValue(initialValue);

    // Update valueToMatch when either operator or value changes
    combineLatest([
      this.state.controls.operatorControl.valueChanges.pipe(
        startWith(initialOperator)
      ),
      this.state.controls.textControl.valueChanges.pipe(
        startWith(initialValue)
      ),
    ]).subscribe(([operator, textValue]) => {
      if (textValue && !Number.isNaN(Number(textValue))) {
        this.valueToMatch = {
          operator: operator || '=',
          value: Number(textValue),
        };
      } else {
        this.valueToMatch = null;
      }
    });

    this.hasErrors$ = this.state.statusChanges.pipe(
      map(() => this.state.invalid),
      startWith(this.state.invalid)
    );
  }

  /**
   * Register this component with the matcher registry
   */
  public static registerNumber(
    registry: MatcherComponentRegistryService
  ): void {
    registry.registerComponent('numberMatch', NumberMatcherComponent);
  }
}
